import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return toast.error('Enter your email');
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      // Silent success (backend also returns success) — but show generic in case of network error
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#faf8f4] flex items-center justify-center px-4" data-testid="forgot-password-page">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl">Forgot Password?</CardTitle>
          <CardDescription>
            Enter your email address. If it's on file, we'll send you a link to reset your password.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sent ? (
            <div className="text-center py-4" data-testid="forgot-sent">
              <CheckCircle className="w-12 h-12 mx-auto text-emerald-600 mb-3" />
              <p className="font-semibold">Check your inbox</p>
              <p className="text-sm text-gray-600 mt-2">
                If an account exists for <strong>{email}</strong>, a reset link is on its way. The link expires in 1 hour.
              </p>
              <Button
                variant="outline"
                className="mt-4 w-full"
                onClick={() => navigate('/agent-portal')}
                data-testid="back-to-login"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />Back to Sign In
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@bespokerealty.net"
                  required
                  data-testid="forgot-email-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#BCA480] hover:bg-[#a89470] text-white"
                disabled={loading}
                data-testid="forgot-submit-btn"
              >
                <Mail className="w-4 h-4 mr-2" />
                {loading ? 'Sending…' : 'Send Reset Link'}
              </Button>
              <div className="text-center">
                <Link to="/agent-portal" className="text-sm text-[#BCA480] hover:underline" data-testid="back-to-login-link">
                  ← Back to Sign In
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPassword;
